import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { parsePlanTiers, pickPlan } from '@theme/selling-plans';

/** @typedef {import('@theme/selling-plans').PlanTierEntry} PlanTierEntry */

/**
 * Flavor mode switch and bundle builder.
 *
 * In `single` mode this component does nothing but stay out of the way —
 * Horizon's variant picker and product form handle the sale. In `bundle` mode
 * it hides the picker, collects up to N flavors into slots, and takes over
 * add-to-cart so the selection goes in as one line item per flavor.
 *
 * The bundle discount itself is NOT applied here: it has to be an automatic
 * discount (or a Shopify Function) configured in admin. The savings badge is
 * merchant-entered copy.
 */
class FlavorBundleComponent extends HTMLElement {
  /**
   * One entry per slot, by position. A removed pick leaves its slot empty
   * (null) rather than shifting the later picks left, so the tile the
   * shopper tapped is the one that clears; the next pick fills the first
   * empty slot. Trailing empties are trimmed.
   * @type {Array<{variantId: string, label: string, image: string, price: number, planPrice: number, plans: PlanTierEntry[] | null} | null>}
   */
  #slots = [];

  /** The picks in slot order, gaps skipped. */
  get #picked() {
    return this.#slots.filter((slot) => slot !== null);
  }

  /** @type {'single' | 'bundle'} */
  #mode = 'single';

  /**
   * Slot count fixed by the pack-size selector (pdp-pack-size.js), or null
   * when the block runs on its own min/max settings.
   * @type {number | null}
   */
  #packSize = null;

  /** The add-to-cart label we last wrote, to tell our copy from Horizon's */
  /** @type {string | null} */
  #bundleButtonText = null;

  /** Horizon's own add-to-cart label, restored when leaving bundle mode */
  /** @type {string | null} */
  #singleButtonText = null;

  /** @type {MutationObserver | null} */
  #buttonObserver = null;

  #abortController = new AbortController();

  connectedCallback() {
    const { signal } = this.#abortController;

    this.addEventListener('click', this.#handleClick, { signal });
    // The "Mix flavors" link lives in Horizon's variant picker, outside this
    // element, so mode switches are delegated at the section
    this.#section?.addEventListener('click', this.#handleModeClick, { signal });

    // The pack-size selector connects first and publishes on the section root
    this.#section?.addEventListener('pdp:pack-change', this.#handlePackChange, { signal });
    const initialPack = Number(this.#section?.dataset.packCount);
    if (initialPack > 0) this.#packSize = initialPack;

    // Capture phase at the document: the add-to-cart button is driven by
    // Horizon's own component, so the click has to be intercepted before it
    // reaches any listener bound on the button or the form.
    document.addEventListener('click', this.#interceptAddToCart, { capture: true, signal });
    document.addEventListener('submit', this.#interceptSubmit, { capture: true, signal });

    // A featured-product section is morphed whole after a variant change,
    // which puts the panel and slots back to their server-rendered (closed,
    // empty) state. The picks live in memory, so re-render once it settles.
    this.#section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    this.#render();
    this.#watchButton();
  }

  disconnectedCallback() {
    this.#abortController.abort();
    this.#buttonObserver?.disconnect();
  }

  /**
   * Horizon morphs the add-to-cart button on variant change, resetting its
   * label — re-apply the bundle copy whenever the button changes under us.
   */
  #watchButton() {
    const buttons = this.#section?.querySelector('.product-details .product-form-buttons');
    if (!buttons) return;
    this.#buttonObserver = new MutationObserver(() => this.#syncAddToCartState());
    this.#buttonObserver.observe(buttons, { childList: true, subtree: true, characterData: true });
  }

  get #maxFlavors() {
    if (this.#packSize) return Math.min(this.#packSize, this.#slotCount);
    return Number(this.dataset.maxFlavors) || 4;
  }

  get #minFlavors() {
    if (this.#packSize) return this.#maxFlavors;
    return Math.min(Number(this.dataset.minFlavors) || 2, this.#maxFlavors);
  }

  /** Slots rendered by Liquid — the pack size can't exceed them */
  get #slotCount() {
    return this.querySelectorAll('.flavor-bundle__slot').length || 1;
  }

  /** @param {Event} event */
  #handlePackChange = (event) => {
    const count = Number(/** @type {CustomEvent} */ (event).detail?.count);
    this.#packSize = count > 0 ? count : null;

    if (this.#slots.length > this.#maxFlavors) {
      this.#slots.length = this.#maxFlavors;
      this.#trimSlots();
    }

    // One pack has nothing to mix: fall back to the single-flavor picker
    if (this.#packSize === 1 && this.#mode === 'bundle') {
      this.#setMode('single');
      return;
    }

    this.#render();
  };

  get #section() {
    return this.closest('.product-information');
  }

  /** @param {Event} event */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    // Only the X removes — clicking the tile itself shouldn't drop a choice
    const remove = target.closest('.flavor-bundle__slot-remove');
    if (remove) {
      const slot = remove.closest('.flavor-bundle__slot');
      if (slot instanceof HTMLElement) {
        const index = Number(slot.dataset.slot);
        if (this.#slots[index]) {
          this.#slots[index] = null;
          this.#trimSlots();
          this.#render();
        }
      }
      return;
    }

    const flavor = target.closest('.flavor-bundle__flavor');
    if (flavor instanceof HTMLElement) {
      if (this.#picked.length >= this.#maxFlavors) return;
      const { variantId, label, image, price, planPrice, plans } = flavor.dataset;
      if (!variantId) return;
      // A flavor can fill only as many slots as the cart will accept of it
      const stock = this.#stockOf(flavor);
      if (stock !== null && (this.#counts().get(variantId) ?? 0) >= stock) return;
      // The first empty slot, which may be a gap left by a removed pick
      let index = this.#slots.findIndex((slot) => slot === null);
      if (index === -1) index = this.#slots.length;
      if (index >= this.#maxFlavors) return;
      this.#slots[index] = {
        variantId,
        label: label ?? '',
        image: image ?? '',
        price: Number(price) || 0,
        planPrice: Number(planPrice) || Number(price) || 0,
        plans: this.#parsePlans(plans),
      };
      this.#render();
    }
  };

  /**
   * Units of a chip's variant still sellable, or null when unlimited.
   * @param {HTMLElement} flavor
   * @returns {number | null}
   */
  #stockOf(flavor) {
    const raw = flavor.dataset.stock;
    if (raw === undefined || raw === '') return null;
    const stock = Number(raw);
    return Number.isFinite(stock) ? Math.max(0, stock) : null;
  }

  /** Drops empty slots off the end so the array ends at the last pick. */
  #trimSlots() {
    while (this.#slots.length > 0 && this.#slots[this.#slots.length - 1] === null) {
      this.#slots.pop();
    }
  }

  /** @param {Event} event */
  #handleModeClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const mode = target.closest('[data-mode]');
    if (!(mode instanceof HTMLElement)) return;
    event.preventDefault();
    this.#setMode(mode.dataset.mode === 'bundle' ? 'bundle' : 'single');
  };

  /** @param {Event} event */
  #handleProductSelect = (event) => {
    const promise = /** @type {CustomEvent & { promise?: Promise<unknown> }} */ (event).promise;
    promise?.then(() => this.#setMode(this.#mode)).catch(() => {});
  };

  /** @param {'single' | 'bundle'} mode */
  #setMode(mode) {
    this.#mode = mode;

    const panel = this.querySelector('[data-ref="panel"]');
    if (panel instanceof HTMLElement) panel.hidden = mode !== 'bundle';

    // Drives the picker/purchase-option visibility from CSS
    this.#section?.setAttribute('data-flavor-mode', mode);

    this.#render();
  }

  #render() {
    const maxFlavors = this.#maxFlavors;
    this.#renderPackCopy(maxFlavors);

    const slots = this.querySelectorAll('.flavor-bundle__slot');
    for (const [index, slot] of [...slots].entries()) {
      const entry = this.#slots[index];
      if (slot instanceof HTMLElement) slot.hidden = index >= maxFlavors;
      slot.classList.toggle('flavor-bundle__slot--filled', Boolean(entry));
      slot.removeAttribute('data-flavor');

      const label = slot.querySelector('.flavor-bundle__slot-label');
      const media = slot.querySelector('.flavor-bundle__slot-media');
      const remove = slot.querySelector('.flavor-bundle__slot-remove');

      if (entry) {
        slot.setAttribute('data-flavor', entry.label);
        if (label) label.textContent = entry.label;
        if (media) media.innerHTML = entry.image ? `<img src="${entry.image}" alt="">` : '';
        if (remove instanceof HTMLElement) {
          remove.hidden = false;
          remove.setAttribute('aria-label', `${this.dataset.removeLabel ?? ''} ${entry.label}`.trim());
        }
      } else {
        if (label) label.textContent = this.#addFlavorLabel;
        if (media) media.innerHTML = '';
        if (remove instanceof HTMLElement) remove.hidden = true;
      }
    }

    const counts = this.#counts();
    for (const flavor of this.querySelectorAll('.flavor-bundle__flavor')) {
      if (!(flavor instanceof HTMLButtonElement)) continue;
      const count = counts.get(flavor.dataset.variantId ?? '') ?? 0;
      const stock = this.#stockOf(flavor);
      // Short stock shows on the chip only once it matters: fewer units
      // than the slots still to fill
      const short = stock !== null && stock < maxFlavors;
      const exhausted = stock !== null && count >= stock;
      flavor.disabled = this.#picked.length >= this.#maxFlavors || exhausted;
      flavor.classList.toggle('flavor-bundle__flavor--added', count > 0);
      flavor.classList.toggle('flavor-bundle__flavor--short', exhausted);
      const badge = flavor.querySelector('.flavor-bundle__flavor-count');
      if (badge instanceof HTMLElement) {
        badge.hidden = count === 0;
        badge.textContent = String(count);
      }
      const stockLabel = flavor.querySelector('[data-ref="flavorStock"]');
      if (stockLabel instanceof HTMLElement) stockLabel.hidden = !short;
    }

    const progress = this.querySelector('[data-ref="progress"]');
    if (progress instanceof HTMLElement) {
      progress.style.width = `${(this.#picked.length / this.#maxFlavors) * 100}%`;
      progress.classList.toggle('flavor-bundle__progress-bar--complete', this.#tierState().isMax);
    }

    const status = this.querySelector('[data-ref="status"]');
    if (status instanceof HTMLElement) {
      status.textContent = this.#statusText;
    }

    this.#syncAddToCartState();
    this.#updatePurchaseTotals();
    this.#broadcast();
  }

  /**
   * Swaps the "up to N" copy for exact counts once the pack-size selector
   * fixes the slot count.
   * @param {number} count
   */
  #renderPackCopy(count) {
    if (!this.#packSize) return;
    /** @param {string | undefined} template */
    const fill = (template) => (template ?? '').replace('[count]', String(count));

    const sub = this.querySelector('[data-ref="bundleSub"]');
    if (sub && this.dataset.bundleSubTemplate) sub.textContent = fill(this.dataset.bundleSubTemplate);

    const hint = this.querySelector('[data-ref="buildHint"]');
    if (hint && this.dataset.buildHintTemplate) hint.textContent = fill(this.dataset.buildHintTemplate);

    if (this.dataset.statusMinTemplate) this.dataset.statusMin = fill(this.dataset.statusMinTemplate);
  }

  /**
   * Tells the purchase card what it is pricing: the mode, and in bundle mode
   * the list price of every filled slot (sibling products differ in price).
   */
  #broadcast() {
    this.#section?.dispatchEvent(
      new CustomEvent('flavor-bundle:change', {
        bubbles: true,
        detail: {
          mode: this.#mode,
          count: this.#picked.length,
          valid: this.#isValid,
          prices: this.#picked.map((slot) => slot.price),
        },
      })
    );
  }

  get #addFlavorLabel() {
    return this.dataset.addFlavorLabel || 'Add flavor';
  }

  /** @returns {Array<{count: number, percent: number}>} ascending by count */
  get #tiers() {
    return (this.dataset.tiers ?? '')
      .split(',')
      .map((pair) => pair.split(':').map((part) => Number(part.trim())))
      .filter(([count, percent]) => Number.isFinite(count) && Number.isFinite(percent))
      .map(([count, percent]) => ({ count, percent }))
      .sort((a, b) => a.count - b.count);
  }

  /** Best tier already earned, and the next one still reachable. */
  #tierState() {
    const tiers = this.#tiers;
    const picked = this.#picked.length;
    const earned = tiers.filter((tier) => picked >= tier.count).at(-1) ?? null;
    const next = tiers.find((tier) => tier.count > picked) ?? null;
    return { earned, next, isMax: Boolean(earned) && !next };
  }

  get #statusText() {
    if (!this.#isValid) return this.dataset.statusMin ?? '';

    const { earned, next, isMax } = this.#tierState();
    if (!earned) return this.dataset.statusReady ?? '';

    if (isMax) {
      return (this.dataset.statusMax ?? '').replace('[percent]', String(earned.percent));
    }

    return (this.dataset.statusTier ?? '')
      .replace('[percent]', String(earned.percent))
      .replace('[remaining]', String(next ? next.count - this.#picked.length : 0))
      .replace('[next]', String(next?.percent ?? ''));
  }

  /** Bundle subtotals, so the purchase rows stop quoting a single unit. */
  #updatePurchaseTotals() {
    const rows = this.#section?.querySelectorAll('.purchase-options__option');
    if (!rows?.length) return;

    const { earned } = this.#tierState();

    for (const row of rows) {
      const now = row.querySelector('.purchase-options__price-now');
      const was = row.querySelector('.purchase-options__price-was');
      const badge = row.querySelector('.purchase-options__badge');
      if (!(now instanceof HTMLElement)) continue;

      if (!now.dataset.singlePrice) now.dataset.singlePrice = now.textContent ?? '';
      if (was instanceof HTMLElement && !was.dataset.singlePrice) {
        was.dataset.singlePrice = was.textContent ?? '';
      }
      // The Liquid-rendered badge carries the base subscription discount,
      // computed from the Recharge selling-plan allocation — stash it so the
      // tier badge can hand back to it outside bundle mode.
      if (badge instanceof HTMLElement && !badge.dataset.singleLabel) {
        badge.dataset.singleLabel = badge.textContent ?? '';
      }

      if (this.#mode !== 'bundle' || this.#picked.length === 0) {
        now.textContent = now.dataset.singlePrice;
        if (was instanceof HTMLElement) was.textContent = was.dataset.singlePrice;
        if (badge instanceof HTMLElement) badge.textContent = badge.dataset.singleLabel;
        continue;
      }

      const subscribe = row.querySelector('input[value="subscribe"]') !== null;
      const picked = this.#picked;
      const listTotal = picked.reduce((sum, slot) => sum + slot.price, 0);
      // An earned bundle tier supersedes the per-line plan price on the
      // subscribe row; below the first tier the Recharge base rate stands.
      let total = picked.reduce((sum, slot) => sum + (subscribe ? slot.planPrice : slot.price), 0);
      if (subscribe && earned) {
        total = Math.round(listTotal * (1 - earned.percent / 100));
      }

      now.textContent = this.#money(total);
      if (was instanceof HTMLElement && listTotal > total) was.textContent = this.#money(listTotal);

      if (badge instanceof HTMLElement && subscribe) {
        const tierLabel = (this.dataset.saveLabel ?? '').replace('[percent]', String(earned?.percent ?? ''));
        badge.textContent = earned && tierLabel ? tierLabel : badge.dataset.singleLabel;
      }
    }
  }

  /** @param {number} cents */
  #money(cents) {
    const amount = (cents / 100).toFixed(2);
    return `$${amount.replace(/\B(?=(\d{3})+(?!\d))/, ',')}`;
  }

  /**
   * Parses a chip's `data-plans` tier map (see snippets/selling-plan-tiers).
   *
   * @param {string | undefined} raw
   * @returns {PlanTierEntry[] | null}
   */
  #parsePlans(raw) {
    const entries = parsePlanTiers(raw);
    return entries.length ? entries : null;
  }

  /** @returns {Map<string, number>} variant id → quantity */
  #counts() {
    const counts = new Map();
    for (const { variantId } of this.#picked) {
      counts.set(variantId, (counts.get(variantId) ?? 0) + 1);
    }
    return counts;
  }

  get #isValid() {
    return this.#picked.length >= this.#minFlavors;
  }

  #syncAddToCartState() {
    const button = this.#addToCartButton;
    if (!(button instanceof HTMLButtonElement)) return;

    // The label is the button's bare text node (icon and total live in spans)
    const textNode = [...button.childNodes].find(
      (node) => node.nodeType === Node.TEXT_NODE && node.textContent?.trim()
    );

    if (this.#mode !== 'bundle') {
      if (button.dataset.bundleDisabled === 'true') {
        button.disabled = false;
        delete button.dataset.bundleDisabled;
      }
      if (textNode && this.#singleButtonText != null && textNode.textContent === this.#bundleButtonText) {
        textNode.textContent = this.#singleButtonText;
      }
      this.#bundleButtonText = null;
      return;
    }

    button.disabled = !this.#isValid;
    button.dataset.bundleDisabled = String(!this.#isValid);

    if (!textNode) return;
    // Anything we didn't write is Horizon's own label (Add to cart / Sold out)
    if (textNode.textContent !== this.#bundleButtonText) this.#singleButtonText = textNode.textContent ?? '';

    const wanted = this.#isValid ? this.#singleButtonText : this.#selectFlavorsText;
    if (wanted != null && textNode.textContent !== wanted) textNode.textContent = wanted;
    this.#bundleButtonText = this.#isValid ? null : wanted;
  }

  /** "Select 2 flavors" for an empty bundle, "Select 1 more flavor" after that */
  get #selectFlavorsText() {
    const remaining = this.#minFlavors - this.#picked.length;
    if (this.#picked.length === 0) {
      return (this.dataset.selectFlavors ?? '').replace('[count]', String(this.#minFlavors));
    }
    if (remaining === 1) return this.dataset.selectMoreOne ?? '';
    return (this.dataset.selectMore ?? '').replace('[count]', String(remaining));
  }

  get #addToCartButton() {
    return this.#section?.querySelector('.product-details .add-to-cart-button') ?? null;
  }

  /** @param {Event} event */
  #interceptSubmit = (event) => {
    if (this.#mode !== 'bundle') return;
    const target = event.target;
    if (!(target instanceof HTMLFormElement) || !this.#section?.contains(target)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.#addBundleToCart();
  };

  /** @param {Event} event */
  #interceptAddToCart = (event) => {
    if (this.#mode !== 'bundle') return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest('.add-to-cart-button');
    if (!button || button !== this.#addToCartButton) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.#addBundleToCart();
  };

  async #addBundleToCart() {
    if (!this.#isValid) return;

    // Purchase options still apply in bundle mode — but selling plan ids are
    // per-product, and a bundle can mix variants from sibling products (the
    // variety packs). The page's hidden selling_plan input only covers its own
    // product, so each line resolves its plan from its chip's tier map: the
    // bundle size picks the tier (a 3-pack bundle is three items shipping
    // together, whatever the split across flavors), the picker the cadence.
    const sellingPlanInput = this.#section?.querySelector('input[name="selling_plan"]');
    const sellingPlan =
      sellingPlanInput instanceof HTMLInputElement && sellingPlanInput.value
        ? Number(sellingPlanInput.value)
        : null;
    const planSelect = this.#section?.querySelector('.purchase-options__plan-select, .pdp-buy__plan-select');
    const ord =
      planSelect instanceof HTMLSelectElement
        ? Number(planSelect.selectedOptions[0]?.dataset.ord ?? planSelect.value) || 0
        : 0;
    const picked = this.#picked;
    const bundleQuantity = picked.length;

    /** @type {Map<string, PlanTierEntry[] | null>} */
    const plansByVariant = new Map(picked.map((slot) => [slot.variantId, slot.plans]));

    const items = [...this.#counts()].map(([id, quantity]) => {
      /** @type {{id: number, quantity: number, selling_plan?: number}} */
      const item = { id: Number(id), quantity };
      if (sellingPlan) {
        const plans = plansByVariant.get(id);
        if (plans && ord) {
          // Same tier map the buy box resolves the page product from, so the
          // one-flavor and bundle paths land on one plan id and one cart line
          const mapped = pickPlan(plans, bundleQuantity, ord) ?? plans.find((entry) => entry.ord === ord);
          if (mapped) item.selling_plan = mapped.id;
          // A chip with a map but no plan at this cadence goes in as one-time
          // rather than failing the whole add with a foreign plan id.
        } else if (!plans) {
          // Chip without a plan map — keep the old single-product behavior
          item.selling_plan = sellingPlan;
        }
      }
      return item;
    });
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : null))
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const result = await response.json();
      if (result.status) throw new Error(result.description || result.message || 'Add to cart failed');

      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(await this.#fetchCart()) });

      this.#slots = [];
      this.#render();
    } catch (error) {
      console.error('[flavor-bundle]', error);
      deferred.reject(error);
    }
  }

  async #fetchCart() {
    /** @type {any} */
    const cartItems = document.querySelector('cart-items-component');
    if (cartItems) {
      await customElements.whenDefined('cart-items-component');
      return cartItems.fetchCartData();
    }
    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    return response.json();
  }
}

if (!customElements.get('flavor-bundle-component')) {
  customElements.define('flavor-bundle-component', FlavorBundleComponent);
}
